import type { AlertColor } from '@mui/material/Alert';
import type { ReactNode } from 'react';
import type { ActionButtonConfig } from '../ActionButtons';
import type { AlertBannerProps } from '../AlertBanner/AlertBanner.model';
import type { BDAlert } from '../BDAlerts';

/**
 * ModalCloseReason: Reasons for closing a modal (Dialog or BDBottomSheet).
 */
export type ModalCloseReason =
  | 'backdropClick'
  | 'escapeKeyDown'
  | 'dragDismiss';

/**
 * DialogTitleProps: Props for the dialog title component.
 */
export interface DialogTitleProps {
  /**
   * Unique ID for the dialog title.
   */
  id: string;

  /**
   * Title content, can be string or ReactNode.
   */
  title: ReactNode | string;

  /**
   * Callback for close action.
   */
  onClose: () => void;

  /**
   * Optional labels for accessibility (e.g., aria-label for close button).
   */
  labels?: {
    close?: string;
  };

  /**
   * Disable the close button.
   * Default: false
   */
  disableClose?: boolean;

  /**
   * Enable extended mobile layout.
   * Default: false
   */
  extendedMobile?: boolean;
}

/**
 * DialogMessage: Message and severity for dialog alerts.
 */
export interface DialogMessage {
  /**
   * Alert message to display in the dialog.
   */
  message: string;

  /**
   * Severity level for the alert (e.g., 'error', 'warning', 'info', 'success').
   */
  severity: AlertColor;
}

/**
 * DialogResult: Result object returned from dialog actions.
 * @template T Entity type
 */
export interface DialogResult<T = never> {
  /**
   * Status of the dialog result (e.g., SAVE, CANCEL, ERROR).
   */
  status: DialogResultStatus;

  /**
   * Optional entity returned from the dialog (e.g., form data).
   */
  entity?: T;
}

/**
 * DialogComponentProps: Props for the dialog component rendered inside BDDialog.
 * @template T Entity type
 */
export interface DialogComponentProps<T = never> {
  /**
   * Callback to close the modal.
   * @param isSuccess - Whether the dialog was closed successfully.
   */
  onCloseModal: (isSuccess: boolean) => void;

  /**
   * Set alerts for the dialog using BDAlert system.
   * @param alerts - Array or updater function for BDAlert objects.
   * @param replace - If true, replaces existing alerts. Default: true
   */
  setAlerts: (
    alerts: BDAlert[] | ((existingAlerts: BDAlert[]) => BDAlert[]),
    replace?: boolean
  ) => void;

  /**
   * Set alert banners for the dialog using AlertBanner system.
   * @param alerts - Array or updater function for AlertBannerProps objects.
   * @param replace - If true, replaces existing alerts. Default: true
   */
  setAlertBanners?: (
    alerts:
      | AlertBannerProps[]
      | ((existingAlerts: AlertBannerProps[]) => AlertBannerProps[]),
    replace?: boolean
  ) => void;

  /**
   * Disable submit button in the dialog.
   * Default: false
   */
  disableSubmit?: boolean;

  /**
   * Entity data passed to the dialog (e.g., form values).
   */
  entity?: T;

  /**
   * Entity ID for the dialog (if applicable).
   */
  entityId?: string;

  /**
   * Callback for dialog result (e.g., form submission).
   */
  onResult?: (result: DialogResult<T>) => void;

  /**
   * Use legacy assign logic (for backward compatibility).
   * Default: false
   */
  useLegacyAssign?: boolean;

  /**
   * Disable entity fields in the dialog.
   * Default: false
   */
  disableEntityFields?: boolean;

  /**
   * Optional redirect path after dialog action.
   */
  redirectPath?: string;
}

/**
 * DialogWrapperProps: Props for the BDDialog wrapper component.
 * @template T Entity type
 */
export interface DialogWrapperProps<T> {
  /**
   * Whether the modal is open.
   * Default: false
   */
  openModal: boolean;

  /**
   * Callback to close the modal.
   * @param isSuccess - Whether the dialog was closed successfully.
   */
  onCloseModal: (isSuccess: boolean) => void;

  /**
   * Dialog component to render inside the modal.
   */
  component: React.ComponentType<DialogComponentProps<T>>;

  /**
   * Dialog size (xs, sm, md, lg, xl).
   * Default: 'md'
   */
  dialogSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Disable submit button in the dialog.
   * Default: false
   */
  disableSubmit?: boolean;

  /**
   * Disable close button in the dialog.
   * Default: false
   */
  disableClose?: boolean;

  /**
   * Entity data passed to the dialog.
   */
  entity?: T;

  /**
   * Entity ID for the dialog (if applicable).
   */
  entityId?: string;

  /**
   * Dialog title content.
   */
  title?: ReactNode | string;

  /**
   * Callback for dialog result (e.g., form submission).
   */
  onResult?: (result: DialogResult<T>) => void;

  /**
   * Button configuration for dialog actions.
   */
  buttonsConfig?: ActionButtonConfig[];
  customActionsContent?: ReactNode;

  /**
   * Use legacy assign logic (for backward compatibility).
   * Default: false
   */
  useLegacyAssign?: boolean;

  /**
   * Disable entity fields in the dialog.
   * Default: false
   */
  disableEntityFields?: boolean;

  /**
   * Accessibility labels for dialog and close button.
   */
  labels?: {
    aria?: string;
    close?: string;
    dragHandle?: string;
  };

  /**
   * Enable extended mobile view for the dialog.
   * Default: false
   */
  extendedMobileView?: boolean;

  /**
   * Use draggable bottom sheet drawer on mobile viewports (≤430px).
   * When enabled, renders content in a swipeable drawer with drag handle.
   * Default: false
   */
  drawerMobileView?: boolean;

  /**
   * When true, dialog/bottom-sheet content uses a flex column layout so nested
   * tables can scroll internally (toolbar, banner, and headers stay fixed).
   * Default: false
   */
  containedContentScroll?: boolean;

  /**
   * Custom class name for the dialog wrapper.
   */
  className?: string;

  /**
   * Data test ID for the dialog wrapper.
   */
  dataTestId?: string;

  /**
   * Disable backdrop click to prevent closing the dialog.
   * Default: false
   */
  disableBackdropClick?: boolean;

  /**
   * Callback fired after the dialog has exited and the exit animation is complete.
   * Use this to safely trigger actions that depend on the dialog being fully closed
   * (e.g., opening another dialog).
   */
  onExited?: () => void;
}

/**
 * DialogResultStatus: Status values for dialog results.
 */
export enum DialogResultStatus {
  /** The dialog was cancelled or closed without saving. */
  CANCEL = 'CANCEL',
  /** The dialog completed a save or submit action. */
  SAVE = 'SAVE',
  /** The dialog performed a delete action. */
  DELETE = 'DELETE',
  /** The dialog performed an edit action. */
  EDIT = 'EDIT',
  /** The dialog encountered an error. */
  ERROR = 'ERROR',
}
